import { getCartCookie } from "@/utils/cart/getCartCookie";
import { useMarket } from "@app/_context";
import type { Cart, GiftCardRemoveResponse } from "@server/gql/graphql";
import { removeGiftCard } from "@server/requests/giftcard/removeGiftCard";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";

type RemoveGiftcard = <T extends boolean = false>(
  paymentCode: string,
  async?: T,
) => T extends true ? Promise<GiftCardRemoveResponse | undefined> : void;

export default function useRemoveGiftcard() {
  const { serviceLocale: locale } = useMarket().state.market;
  const queryClient = useQueryClient();

  const { mutate, mutateAsync, isError, isPending, isSuccess } = useMutation({
    mutationFn: async (paymentCode: string) => {
      const cartId = getCartCookie()?.value;
      if (!cartId) return Promise.resolve(undefined);
      return removeGiftCard({
        paymentCode,
        locale,
        cartId,
      });
    },
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: ["cart"] });
      await queryClient.cancelQueries({ queryKey: ["checkout"] });
      await queryClient.cancelQueries({ queryKey: ["checkoutDelivery"] });
      await queryClient.cancelQueries({ queryKey: ["payment"] });

      const previousCart = queryClient.getQueryData<Cart>(["cart"]);
      return { previousCart };
    },
    onError: (
      error,
      input,
      context: { previousCart: Cart | undefined } | undefined,
    ) => {
      if (context?.previousCart) {
        queryClient.setQueryData<Cart>(["cart"], context.previousCart);
      }
    },
    onSettled: async () => {
      void queryClient.invalidateQueries({ queryKey: ["cart"] });
      void queryClient.invalidateQueries({ queryKey: ["checkout"] });
      void queryClient.invalidateQueries({ queryKey: ["checkoutDelivery"] });
      await queryClient.invalidateQueries({ queryKey: ["payment"] });
    },
  });

  const removeGiftcard = useCallback(
    (paymentCode: string, async = false) => {
      if (async) return mutateAsync(paymentCode);
      return mutate(paymentCode);
    },
    [mutate, mutateAsync],
  );

  return {
    removeGiftcard: removeGiftcard as RemoveGiftcard,
    isErrorRemoveGiftcard: isError,
    isPendingRemoveGiftcard: isPending,
    isSuccessRemoveGiftcard: isSuccess,
  };
}
